import React from "react";
import { Button } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  findApplicableFascia,
  isValidTimePoint,
} from "../../utils/fasceSelezioneOreUtils";

const TimeChipSelector = ({
  timeSlots = [],
  tempTimeRange,
  onTimeSlotClick,
  onClearTimeRange,
  showConfirmButton = false,
  onTimeRangeConfirm,
  minHour,
  maxHour,
  minOreAcquistabili,
  maxOreAcquistabiliAlGiorno,
  permettiEccezioneMinOreInizio,
  permettiEccezioneMinOreFine,
  data,
  unavailableRange,
  fasceSelezioneOre = [],
  currentDate = null,
}) => {
  const { t } = useTranslation();

  // Helper functions
  const parseTimeString = (timeString) => {
    if (typeof timeString === "string") {
      const [hours, minutes] = timeString
        .split(":")
        .map((num) => parseInt(num, 10));
      return { hours, minutes: minutes || 0 };
    }
    return { hours: timeString, minutes: 0 };
  };

  const getHourFromTimeString = (timeString) => {
    const { hours, minutes } = parseTimeString(timeString);
    return hours + minutes / 60;
  };

  // Helper function to adjust time to match fascia rules
  const minHourNum = getHourFromTimeString(minHour);
  const maxHourNum = getHourFromTimeString(maxHour);

  // Generate time chips (30-minute intervals)
  const generateTimeChips = () => {
    const chips = [];
    const startHour = Math.floor(minHourNum);
    const endHour = Math.ceil(maxHourNum);

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 30) {
        const timeValue = hour + minutes / 60;
        if (timeValue >= minHourNum && timeValue < maxHourNum) {
          const timeStr = `${hour.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;

          // Show all chips - tipoOrarioIntero will control selectability, not visibility

          const nextMinutes = minutes + 30;
          const nextHour = nextMinutes >= 60 ? hour + 1 : hour;
          const nextMins = nextMinutes >= 60 ? 0 : nextMinutes;
          const endStr = `${nextHour.toString().padStart(2, "0")}:${nextMins
            .toString()
            .padStart(2, "0")}`;

          chips.push({
            start: timeStr,
            end: endStr,
            display: timeStr.slice(0, 5),
            value: timeValue,
          });
        }
      }
    }
    return chips;
  };

  // Get availability status for a time chip
  const getChipAvailability = (chip) => {
    const chipTime = chip.value;

    // Before minHour or after maxHour - disabled
    if (chipTime < minHourNum || chipTime >= maxHourNum) {
      return "disabled";
    }

    // Check if it's in available time ranges
    // Handle both {start, end} and {dalle, alle} formats
    const isAvailable = timeSlots.some((timeSlot) => {
      const rangeStart = getHourFromTimeString(
        timeSlot.start || timeSlot.dalle
      );
      const rangeEnd = getHourFromTimeString(timeSlot.end || timeSlot.alle);
      return chipTime >= rangeStart && chipTime < rangeEnd;
    });

    return isAvailable ? "available" : "blocked";
  };

  // Check if a chip can be selected based on business rules
  const canSelectChip = (chip) => {
    const availability = getChipAvailability(chip);
    if (availability !== "available") return false;

    const chipTime = chip.value;
    const chipEndTime = chipTime + 0.5; // Each chip represents a 30-minute slot

    // Get fascia rules
    const fascia = findApplicableFascia(
      fasceSelezioneOre,
      currentDate,
      chip.start
    );

    // Check fascia rules - only validate start point if no selection exists yet
    if (!tempTimeRange || !tempTimeRange.start) {
      if (fascia && !isValidTimePoint(chip.start, fascia.tipoOrarioIntero)) {
        return false; // Starting chip doesn't match the required time point type
      }
    } else {
      // If we have a selection, check if clicking this chip would create a valid duration
      const startTime = getHourFromTimeString(tempTimeRange.start);

      // Calculate what the duration would be if we extend to this chip
      if (chipTime < startTime) {
        // Extending/moving start backward - duration from new start to current end
        const potentialEndTime = getHourFromTimeString(tempTimeRange.end);
        const duration = potentialEndTime - chipTime;

        // Check if this duration is valid according to soloOreIntere
        if (fascia && fascia.soloOreIntere) {
          const isWholeHour = Math.abs(duration - Math.round(duration)) < 0.01;
          if (!isWholeHour) return false;
        }
      } else if (chipTime >= getHourFromTimeString(tempTimeRange.end)) {
        // Extending end forward - duration from current start to end of clicked chip
        const duration = chipEndTime - startTime;

        // Check if this duration is valid according to soloOreIntere
        if (fascia && fascia.soloOreIntere) {
          const isWholeHour = Math.abs(duration - Math.round(duration)) < 0.01;
          if (!isWholeHour) return false;
        }
      }
      // If clicking within range, it's a restart, which will be handled later
    }

    // If no selection yet, check if we can start here
    if (!tempTimeRange || !tempTimeRange.start) {
      const containingRange = timeSlots.find((range) => {
        const rangeStart = getHourFromTimeString(range.start || range.dalle);
        const rangeEnd = getHourFromTimeString(range.end || range.alle);
        return chipTime >= rangeStart && chipTime < rangeEnd;
      });

      if (!containingRange) return false;

      // Get fascia rules for this time and adjust minOreAcquistabili
      const fascia = findApplicableFascia(
        fasceSelezioneOre,
        currentDate,
        chip.start
      );
      const adjustedMinOre = fascia?.soloOreIntere
        ? Math.ceil(minOreAcquistabili)
        : minOreAcquistabili;

      const rangeEnd = getHourFromTimeString(
        containingRange.end || containingRange.alle
      );
      const wouldEndAt = chipTime + adjustedMinOre;

      if (wouldEndAt > rangeEnd) return false;

      if (!permettiEccezioneMinOreInizio && minOreAcquistabili) {
        const rangeStart = getHourFromTimeString(
          containingRange.start || containingRange.dalle
        );
        const gapAtStart = chipTime - rangeStart;
        if (gapAtStart > 0 && gapAtStart < minOreAcquistabili) return false;
      }

      if (!permettiEccezioneMinOreFine && minOreAcquistabili) {
        const gapAtEnd = rangeEnd - wouldEndAt;
        if (gapAtEnd > 0 && gapAtEnd < minOreAcquistabili) return false;
      }

      return true;
    }

    // If we already have a range
    if (tempTimeRange.start && tempTimeRange.end) {
      const currentStartTime = getHourFromTimeString(tempTimeRange.start);
      const currentEndTime = getHourFromTimeString(tempTimeRange.end);

      let newStartTime, newEndTime;

      if (chipTime < currentStartTime) {
        newStartTime = chipTime;
        newEndTime = currentEndTime;
      } else if (chipTime >= currentEndTime) {
        newStartTime = currentStartTime;
        newEndTime = chipTime + 0.5;
      } else {
        // Clicking within range - restart
        const containingRange = timeSlots.find((range) => {
          const rangeStart = getHourFromTimeString(range.start);
          const rangeEnd = getHourFromTimeString(range.end);
          return chipTime >= rangeStart && chipTime < rangeEnd;
        });

        if (!containingRange) return false;

        // Get fascia rules for this time and adjust minOreAcquistabili
        const chipFascia = findApplicableFascia(
          fasceSelezioneOre,
          currentDate,
          chip.start
        );
        const adjustedMinOre = chipFascia?.soloOreIntere
          ? Math.ceil(minOreAcquistabili)
          : minOreAcquistabili;

        const rangeStart = getHourFromTimeString(containingRange.start);
        const rangeEnd = getHourFromTimeString(containingRange.end);
        const proposedEnd = chipTime + adjustedMinOre;

        if (proposedEnd > rangeEnd) return false;

        if (!permettiEccezioneMinOreInizio && minOreAcquistabili) {
          const gapAtStart = chipTime - rangeStart;
          if (gapAtStart > 0 && gapAtStart < minOreAcquistabili) return false;
        }

        if (!permettiEccezioneMinOreFine && minOreAcquistabili) {
          const gapAtEnd = rangeEnd - proposedEnd;
          if (gapAtEnd > 0 && gapAtEnd < minOreAcquistabili) return false;
        }

        return true;
      }

      const newDuration = newEndTime - newStartTime;
      if (newDuration < minOreAcquistabili) return false;
      if (newDuration > maxOreAcquistabiliAlGiorno) return false;

      // NOTE: Don't check soloOreIntere here - allow intermediate durations while building selection
      // The final validation will happen when the user confirms the selection

      // Check continuous availability
      for (let time = newStartTime; time < newEndTime; time += 0.5) {
        const isAvailable = timeSlots.some((range) => {
          const rangeStart = getHourFromTimeString(range.start);
          const rangeEnd = getHourFromTimeString(range.end);
          return time >= rangeStart && time < rangeEnd;
        });
        if (!isAvailable) return false;
      }

      return true;
    }

    return false;
  };

  // Check if chip is selected
  const isChipSelected = (chip) => {
    if (!tempTimeRange || !tempTimeRange.start) return false;
    if (!tempTimeRange.end) {
      return chip.start === tempTimeRange.start;
    }
    return chip.start >= tempTimeRange.start && chip.start < tempTimeRange.end;
  };

  // Handle chip click
  const handleChipClick = (chip) => {
    const canSelect = canSelectChip(chip);
    if (!canSelect) return;

    // If first click, set range to minOreAcquistabili
    if (!tempTimeRange || !tempTimeRange.start) {
      const chipTime = chip.value;

      // Get applicable fascia and calculate increment
      const fascia = findApplicableFascia(
        fasceSelezioneOre,
        currentDate,
        chip.start
      );
      const increment = fascia?.soloOreIntere ? 1.0 : 0.5;

      // Calculate end time using the appropriate increment
      let endTime = chipTime + Math.max(minOreAcquistabili, increment);

      // Adjust to match fascia rules if they exist
      if (fascia) {
        // Round endTime to match the increment
        endTime = Math.ceil(endTime / increment) * increment;
      }

      const endHour = Math.floor(endTime);
      const endMinute = (endTime % 1) * 60;
      const endTimeString = `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`;

      onTimeSlotClick({
        ...chip,
        end: endTimeString,
        _isFullRange: true,
        isCustomRange: true,
      });
      return;
    }

    // Check if clicking within current range
    if (tempTimeRange.start && tempTimeRange.end) {
      const chipTime = chip.value;
      const currentStartTime = getHourFromTimeString(tempTimeRange.start);
      const currentEndTime = getHourFromTimeString(tempTimeRange.end);

      if (chipTime >= currentStartTime && chipTime < currentEndTime) {
        // Restart with minOreAcquistabili

        // Get applicable fascia and calculate increment
        const fascia = findApplicableFascia(
          fasceSelezioneOre,
          currentDate,
          chip.start
        );
        const increment = fascia?.soloOreIntere ? 1.0 : 0.5;

        // Calculate end time using the appropriate increment
        let endTime = chipTime + Math.max(minOreAcquistabili, increment);

        // Adjust to match fascia rules if they exist
        if (fascia) {
          endTime = Math.ceil(endTime / increment) * increment;
        }

        const endHour = Math.floor(endTime);
        const endMinute = (endTime % 1) * 60;
        const endTimeString = `${endHour
          .toString()
          .padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

        onTimeSlotClick({
          start: chip.start,
          end: endTimeString,
          _isFullRange: true,
          isCustomRange: true,
        });
        return;
      }

      // Extending range
      let newStart, newEnd;
      if (chipTime < currentStartTime) {
        newStart = chip.start;
        newEnd = tempTimeRange.end;
      } else {
        newStart = tempTimeRange.start;
        newEnd = chip.end;
      }

      onTimeSlotClick({
        start: newStart,
        end: newEnd,
        _isFullRange: true,
        isCustomRange: true,
      });
      return;
    }

    onTimeSlotClick(chip);
  };

  // Get chip style
  const getChipStyle = (chip) => {
    const availability = getChipAvailability(chip);
    const isSelected = isChipSelected(chip);
    const canSelect = canSelectChip(chip);

    if (isSelected) {
      return {
        backgroundColor: "#2185d0",
        color: "white",
        border: "1px solid #2185d0",
      };
    }

    if (availability === "blocked") {
      return {
        backgroundColor: "#f0f0f0",
        color: "#999",
        border: "1px solid #ddd",
        textDecoration: "line-through",
      };
    }

    if (availability === "disabled") {
      return {
        backgroundColor: "#333",
        color: "#666",
        border: "1px solid #333",
      };
    }

    if (!canSelect) {
      return {
        backgroundColor: "#fff3cd",
        color: "#856404",
        border: "1px solid #ffc107",
        opacity: 0.6,
      };
    }

    return {
      backgroundColor: "#b8e0d2",
      color: "#0a3d2c",
      border: "1px solid #95d5b2",
    };
  };

  const chips = generateTimeChips();

  return (
    <div style={{ marginTop: "20px", marginBottom: "20px" }}>
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3
          style={{
            textAlign: "center",
            margin: "0 0 10px 0",
            fontSize: "1.5em",
            fontWeight: "bold",
          }}
        >
          {t("seleziona_orario") || "SELECT TIME RANGE"}
        </h3>

        {data && (
          <div
            style={{
              textAlign: "center",
              fontSize: "1.8em",
              fontWeight: "bold",
              color: "#2185d0",
              marginBottom: "15px",
              padding: "10px",
              backgroundColor: "#f0f8ff",
              borderRadius: "8px",
              border: "2px solid #2185d0",
            }}
          >
            {data}
          </div>
        )}

        <div
          style={{
            textAlign: "center",
            fontSize: "0.9em",
            color: "#666",
            marginBottom: "15px",
          }}
        >
          {t("orario_giornaliero") || "Daily Hours"}:{" "}
          {moment(minHour, "HH:mm").format("HH:mm")} -{" "}
          {moment(maxHour, "HH:mm").format("HH:mm")}
        </div>

        {/* Unavailable time warning */}
        {unavailableRange && (
          <div
            style={{
              border: "2px solid #dc3545",
              borderRadius: "8px",
              padding: "10px",
              marginBottom: "15px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#dc3545",
                fontWeight: "bold",
                fontSize: "0.95em",
              }}
            >
              {t("non_disponibile") || "UNAVAILABLE"}:{" "}
              {moment(unavailableRange.start, "HH:mm").format("HH:mm")} -{" "}
              {moment(unavailableRange.end, "HH:mm").format("HH:mm")}
            </div>
          </div>
        )}

        {/* Time chips grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          {chips.map((chip, index) => {
            const chipStyle = getChipStyle(chip);
            const canClick = canSelectChip(chip);
            const availability = getChipAvailability(chip);

            return (
              <div
                key={index}
                onClick={() => canClick && handleChipClick(chip)}
                style={{
                  ...chipStyle,
                  padding: "12px 8px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontSize: "0.95em",
                  fontWeight: "bold",
                  cursor:
                    availability === "available" && canClick
                      ? "pointer"
                      : "not-allowed",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: "1em", lineHeight: "1.2" }}>
                  {chip.display}
                </span>
                <span
                  style={{
                    fontSize: "0.75em",
                    lineHeight: "1.2",
                    marginTop: "2px",
                  }}
                >
                  {chip.end}
                </span>
              </div>
            );
          })}
        </div>

        {/* Selection summary */}
        {tempTimeRange && tempTimeRange.start && tempTimeRange.end && (
          <div
            style={{
              border: "2px solid #000",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "1.1em", marginBottom: "5px" }}>
              {t("selezionato") || "Selected"}:{" "}
              <strong>
                {moment(tempTimeRange.start, "HH:mm").format("HH:mm")} -{" "}
                {moment(tempTimeRange.end, "HH:mm").format("HH:mm")}
              </strong>
            </div>
            <div style={{ fontSize: "0.9em", color: "#666" }}>
              (
              {(
                getHourFromTimeString(tempTimeRange.end) -
                getHourFromTimeString(tempTimeRange.start)
              ).toFixed(1)}{" "}
              {t("ore") || "hours"})
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          {tempTimeRange && tempTimeRange.start && (
            <Button
              color="red"
              onClick={onClearTimeRange}
              style={{ flex: 1, maxWidth: "200px" }}
            >
              {t("cancella") || "Cancel"}
            </Button>
          )}

          {showConfirmButton &&
            tempTimeRange &&
            tempTimeRange.start &&
            tempTimeRange.end && (
              <Button
                primary
                onClick={onTimeRangeConfirm}
                style={{ flex: 1, maxWidth: "200px" }}
                size="large"
              >
                {t("conferma_prenotazione") || "CONFIRM BOOKING"}
              </Button>
            )}
        </div>
      </div>
    </div>
  );
};

export default TimeChipSelector;
